import React from 'react';
import { DragLayerMonitor, useDragLayer, XYCoord } from 'react-dnd';
import { DragSourceType, EntityType } from 'const';
import * as Models from 'models';
import { ArtboardLayoutDragObject, AssetDragObject, EmptyLayoutDragObject, LayoutDragObject, ScreenDragObject, StoryCardDragObject } from 'models';
import AnchorDragPreview from './Previews/Anchor';
import CitationItemDragPreview from './Previews/CitationItem';
import ImageListItemDragPreview from './Previews/ImageListItem';
import LayoutListItemDragPreview from './Previews/LayoutListItem';
import LayoutListItemRenditionDragPreview from './Previews/LayoutListItemRendition';
import SSIElementDragPreview from './Previews/SSIElement';
import StoryCardDragPreview from './Previews/StoryCard';
import TextComponentItemDragPreview from './Previews/TextComponentItem';
import styles from './styles.module.scss';

type ItemType = ArtboardLayoutDragObject | AssetDragObject | EmptyLayoutDragObject | ScreenDragObject | StoryCardDragObject | LayoutDragObject;

type CollectedProps = {
  item: ItemType;
  itemType: DragSourceType;
  currentOffset: XYCoord | null;
  isDragging: boolean;
};

const getItemStyles = (currentOffset: CollectedProps['currentOffset']): React.CSSProperties => {
  if (!currentOffset) {
    return { display: 'none' };
  }
  const { x, y } = currentOffset;
  const transform = `translate(${x}px, ${y}px)`;

  return {
    transform,
    WebkitTransform: transform,
  };
};

const renderAssetPreview = (item: Models.AssetDragComponent): JSX.Element | null => {
  if (!item) {
    return null;
  }
  const { type, component } = item;

  switch (type) {
    case EntityType.ANCHOR: return <AnchorDragPreview />;
    case EntityType.TEXT: return <TextComponentItemDragPreview />;
    case EntityType.REFERENCE_CITATION: return <CitationItemDragPreview />;
    case EntityType.IMAGE: {
      const internalInfo = (component as Models.ImageMap).get('_internalInfo');
      const imageSrc = internalInfo?.get('source');

      return <ImageListItemDragPreview imageSrc={imageSrc} />;
    }
    case EntityType.LAYOUT_RENDITION: {
      const imageSrc = (component as Models.ReusableLayoutMap).get('_thumbnailUrl') ?? '';

      return <LayoutListItemRenditionDragPreview imageSrc={imageSrc} />;
    }
    default: return null;
  }
};

const renderStoryCardPreview = (item: Models.StoryCardDragObject): JSX.Element | null => {
  if (!item) {return null;}
  const { screenName, previewUrl } = item;

  return <StoryCardDragPreview screenName={screenName} previewUrl={previewUrl} />;
};

const renderReusableLayoutPreview = (item: Models.LayoutDragObject): JSX.Element | null => {
  if (!item) {return null;}
  const { document } = item;

  return (
    <LayoutListItemDragPreview
      document={document}
      thumbnailUrl={(document as Models.ReusableLayoutMap).get('_thumbnailUrl')} />
  );
};

const renderDragItemPreview = (itemType: DragSourceType | null | undefined, item: ItemType): JSX.Element | null => {
  switch (itemType) {
    case DragSourceType.ASSET: return renderAssetPreview(item as Models.AssetDragComponent);
    case DragSourceType.ARTBOARD_SSI: return <SSIElementDragPreview />;
    case DragSourceType.STORY_CARD: return renderStoryCardPreview(item as Models.StoryCardDragObject);
    case DragSourceType.GROUP_LAYOUT:
    case DragSourceType.REUSABLE_LAYOUT: return renderReusableLayoutPreview(item as Models.LayoutDragObject);
    // define custom drag preview for any drag source here, e.g.
    // case DragSourceType.ARTBOARD_LAYOUT: return <ArtboardLayoutDragPreview dragItem={this.props.item} />;
    default: return null;
  }
};

const DragLayer: React.FC = () => {
  const { item, itemType, currentOffset, isDragging } = useDragLayer<CollectedProps>(
    (monitor: DragLayerMonitor) => ({
      item: monitor.getItem(),
      itemType: monitor.getItemType() as DragSourceType,
      currentOffset: monitor.getClientOffset(),
      isDragging: monitor.isDragging(),
    }),
  );
  if (!isDragging) {
    return null;
  }

  return (
    <div className={styles.DragLayer}>
      <div style={getItemStyles(currentOffset)}>
        {renderDragItemPreview(itemType, item)}
      </div>
    </div>
  );
};

export default DragLayer;
